import { Product } from '../types';
import { CATEGORIES } from '../constants';
import { cleanCategoryName } from '../lib/utils';

export interface NavigationTargetSummary {
  type: 'collection' | 'category' | 'product' | 'external' | 'none';
  label: string;
  subLabel?: string;
  iconName: string;
  colorClass: string;
  bgClass: string;
}

export const SPECIAL_COLLECTIONS = [
  { id: 'bestsellers', value: 'collection:bestsellers', name: 'Best Sellers', icon: '🔥', description: 'Popular & Top Rated Items', color: 'amber' },
  { id: 'trending', value: 'collection:trending', name: 'Trending Items', icon: '⚡', description: 'Hot & In-Demand Right Now', color: 'violet' },
  { id: 'newarrivals', value: 'collection:newarrivals', name: 'New Arrivals', icon: '✨', description: 'Fresh Product Launches', color: 'blue' },
  { id: 'offers', value: 'collection:offers', name: 'Special Offers', icon: '🏷️', description: 'Discounted & Promotional Products', color: 'emerald' },
];

export function getNavigationTargetSummary(linkUrl: string | undefined, products: Product[] = []): NavigationTargetSummary {
  if (!linkUrl || !linkUrl.trim()) {
    return {
      type: 'none',
      label: 'No Link Configured',
      subLabel: 'Clicking banner will not trigger navigation',
      iconName: 'AlertCircle',
      colorClass: 'text-gray-400',
      bgClass: 'bg-gray-100 border-gray-200'
    };
  }

  const url = linkUrl.trim();
  const urlLower = url.toLowerCase();

  // 1. External or Absolute Link
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/')) {
    return {
      type: 'external',
      label: 'External Web Link',
      subLabel: url,
      iconName: 'ExternalLink',
      colorClass: 'text-blue-600',
      bgClass: 'bg-blue-50 border-blue-200'
    };
  }

  // 2. Special Collection
  if (urlLower.startsWith('collection:') || ['offers', 'special offers', 'bestsellers', 'best sellers', 'trending', 'newarrivals', 'new arrivals'].includes(urlLower)) {
    const colKey = urlLower.replace('collection:', '').replace(/\s+/g, '').replace('-', '');
    if (colKey.includes('bestseller') || colKey.includes('best')) {
      return {
        type: 'collection',
        label: 'Best Sellers Collection',
        subLabel: '🔥 Top Selling Products Feed',
        iconName: 'Flame',
        colorClass: 'text-amber-600',
        bgClass: 'bg-amber-50 border-amber-200'
      };
    }
    if (colKey.includes('trending')) {
      return {
        type: 'collection',
        label: 'Trending Collection',
        subLabel: '⚡ Popular In-Demand Items',
        iconName: 'Zap',
        colorClass: 'text-violet-600',
        bgClass: 'bg-violet-50 border-violet-200'
      };
    }
    if (colKey.includes('new') || colKey.includes('arrival')) {
      return {
        type: 'collection',
        label: 'New Arrivals Collection',
        subLabel: '✨ Latest Store Additions',
        iconName: 'Sparkles',
        colorClass: 'text-blue-600',
        bgClass: 'bg-blue-50 border-blue-200'
      };
    }
    if (colKey.includes('offer')) {
      return {
        type: 'collection',
        label: 'Special Offers Collection',
        subLabel: '🏷️ Discounted & Deal Items',
        iconName: 'Tag',
        colorClass: 'text-emerald-600',
        bgClass: 'bg-emerald-50 border-emerald-200'
      };
    }
  }

  // 3. Specific Product
  if (urlLower.startsWith('product:')) {
    const productId = url.substring(url.indexOf(':') + 1).trim();
    const product = products.find(p => p.id === productId);
    if (product) {
      return {
        type: 'product',
        label: `Product: ${product.name}`,
        subLabel: `📦 ₹${product.price} • ${cleanCategoryName(product.category)}`,
        iconName: 'Package',
        colorClass: 'text-brand-accent',
        bgClass: 'bg-brand-accent/10 border-brand-accent/20'
      };
    }
    return {
      type: 'product',
      label: `Product ID: ${productId}`,
      subLabel: 'Opens specific item modal on click',
      iconName: 'Package',
      colorClass: 'text-brand-accent',
      bgClass: 'bg-brand-accent/10 border-brand-accent/20'
    };
  }

  // Check if link matches a product ID directly
  const directProduct = products.find(p => p.id === url);
  if (directProduct) {
    return {
      type: 'product',
      label: `Product: ${directProduct.name}`,
      subLabel: `📦 ₹${directProduct.price} • ${cleanCategoryName(directProduct.category)}`,
      iconName: 'Package',
      colorClass: 'text-brand-accent',
      bgClass: 'bg-brand-accent/10 border-brand-accent/20'
    };
  }

  // 4. Category
  const categoryClean = urlLower.replace('category:', '').trim();
  const matchedCategory = CATEGORIES.find(cat => {
    const cClean = cleanCategoryName(cat).toLowerCase();
    const cLower = cat.toLowerCase();
    return cClean === categoryClean || cLower === categoryClean || cClean.includes(categoryClean) || categoryClean.includes(cClean);
  });

  if (matchedCategory) {
    return {
      type: 'category',
      label: `Category: ${cleanCategoryName(matchedCategory)}`,
      subLabel: '📁 Filter homepage feed by this category',
      iconName: 'Grid',
      colorClass: 'text-indigo-600',
      bgClass: 'bg-indigo-50 border-indigo-200'
    };
  }

  // Fallback
  return {
    type: 'external',
    label: `Link Target: ${url}`,
    subLabel: 'Custom link or category name',
    iconName: 'ExternalLink',
    colorClass: 'text-gray-700',
    bgClass: 'bg-gray-50 border-gray-200'
  };
}

export function handleGlobalNavigationClick(
  linkUrl: string | undefined,
  {
    onSelectCategory,
    onSelectCollection,
    onSelectProduct,
    masterProductsList = [],
  }: {
    onSelectCategory?: (cat: string | null) => void;
    onSelectCollection?: (col: 'bestsellers' | 'trending' | 'newarrivals' | 'offers' | null) => void;
    onSelectProduct?: (product: Product) => void;
    masterProductsList?: Product[];
  }
) {
  if (!linkUrl) return;
  const url = linkUrl.trim();
  if (!url) return;

  // 1. External URL or protocol link
  if (url.startsWith('http://') || url.startsWith('https://')) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }

  const urlLower = url.toLowerCase();

  // 2. Check if Special Collection
  if (urlLower.startsWith('collection:') || ['offers', 'special offers', 'bestsellers', 'best sellers', 'trending', 'newarrivals', 'new arrivals'].includes(urlLower)) {
    const colKey = urlLower.replace('collection:', '').replace(/\s+/g, '').replace('-', '');
    let targetCol: 'bestsellers' | 'trending' | 'newarrivals' | 'offers' | null = null;
    if (colKey.includes('offer')) targetCol = 'offers';
    else if (colKey.includes('bestseller') || colKey.includes('best')) targetCol = 'bestsellers';
    else if (colKey.includes('trending')) targetCol = 'trending';
    else if (colKey.includes('new') || colKey.includes('arrival')) targetCol = 'newarrivals';

    if (targetCol && onSelectCollection) {
      onSelectCollection(targetCol);
      setTimeout(() => {
        const el = document.getElementById('categories-section') || document.getElementById('products-section');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 50);
      return;
    }
  }

  // 3. Check if Product target
  if (urlLower.startsWith('product:')) {
    const productId = url.substring(url.indexOf(':') + 1).trim();
    const matchedProduct = masterProductsList.find(p => p.id === productId || p.id === url);
    if (matchedProduct && onSelectProduct) {
      onSelectProduct(matchedProduct);
      return;
    }
  } else {
    const matchedProduct = masterProductsList.find(p => p.id === url);
    if (matchedProduct && onSelectProduct) {
      onSelectProduct(matchedProduct);
      return;
    }
  }

  // 4. Check if Category target
  const cleanUrlCategory = urlLower.replace('category:', '').trim();
  const matchedCategory = CATEGORIES.find(cat => {
    const catClean = cleanCategoryName(cat).toLowerCase();
    const catLower = cat.toLowerCase();
    return catClean === cleanUrlCategory || catLower === cleanUrlCategory || catClean.includes(cleanUrlCategory) || cleanUrlCategory.includes(catClean);
  });

  if (matchedCategory && onSelectCategory) {
    onSelectCategory(matchedCategory);
    setTimeout(() => {
      const el = document.getElementById('categories-section') || document.getElementById('products-section');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 50);
    return;
  }

  // 5. Fallback for relative paths or non-protocol domain links
  if (url.startsWith('/')) {
    window.location.href = url;
  } else if (url.includes('.') && !url.includes(' ')) {
    window.open(`https://${url}`, '_blank', 'noopener,noreferrer');
  } else {
    console.warn("No matching category, collection, or product found for:", url);
  }
}
